import type { Chart } from "../Chart";
import { nicePriceStep, isNewDay, isNewMonth, formatTimeOfDay, formatTimeFull, formatDateShort, formatMonthYear } from "../util";

export interface PriceTick {
  price: number;
  y: number;
}

export function computePriceTicks(chart: Chart): PriceTick[] {
  const vp = chart.viewport;
  const range = vp.priceMax - vp.priceMin;
  if (range <= 0) return [];
  const targetTicks = Math.max(2, Math.floor(vp.plot.height / 58));
  const step = nicePriceStep(range, targetTicks);
  const first = Math.ceil(vp.priceMin / step) * step;
  const ticks: PriceTick[] = [];
  for (let p = first; p <= vp.priceMax; p += step) {
    ticks.push({ price: p, y: vp.yOfPrice(p) });
  }
  return ticks;
}

export interface TimeTick {
  index: number;
  x: number;
  time: number;
  label: string;
  major: boolean;
}

const TIME_STEPS = [
  1, 5, 10, 15, 30,
  60, 2 * 60, 5 * 60, 10 * 60, 15 * 60, 30 * 60,
  3600, 2 * 3600, 4 * 3600, 6 * 3600, 12 * 3600,
  86400, 7 * 86400, 30 * 86400,
];

function niceTimeStep(targetSeconds: number): number {
  for (const s of TIME_STEPS) if (s >= targetSeconds) return s;
  return TIME_STEPS[TIME_STEPS.length - 1];
}

// Time ticks over the collapsed index space. A tick is placed at the first bar
// whose wall-clock time crosses a "nice" boundary. A minimum pixel gap between
// ticks is enforced so labels never cluster, no matter how far out you zoom.
export function computeTimeTicks(chart: Chart): TimeTick[] {
  const data = chart.data;
  if (!data) return [];
  const vp = chart.viewport;
  const [lo, hi] = vp.visibleRange();
  if (hi <= lo) return [];

  const targetSeconds = (105 * chart.tfSeconds) / vp.barSpacing;
  const step = niceTimeStep(targetSeconds);
  const dayView = step >= 86400;
  const minGapPx = 62; // hard floor on label spacing

  const ticks: TimeTick[] = [];
  let prevBucket = NaN;
  let prevTime = NaN;
  let lastX = -Infinity;
  for (let i = lo; i < hi; i++) {
    const t = data.timeAt(i);
    if (!isFinite(t)) continue;
    if (!isFinite(prevBucket)) {
      prevBucket = Math.floor(t / step);
      prevTime = t;
      continue;
    }
    const bucket = Math.floor(t / step);
    if (bucket !== prevBucket) {
      const x = vp.xCenterOfIndex(i);
      if (x - lastX >= minGapPx) {
        const newDay = isNewDay(prevTime, t);
        let label: string;
        let major: boolean;
        if (dayView) {
          label = isNewMonth(prevTime, t) ? formatMonthYear(t) : formatDateShort(t);
          major = true;
        } else if (newDay) {
          label = formatDateShort(t);
          major = true;
        } else {
          label = chart.tfSeconds < 60 && step < 60 ? formatTimeFull(t) : formatTimeOfDay(t);
          major = false;
        }
        ticks.push({ index: i, x, time: t, label, major });
        lastX = x;
      }
      prevBucket = bucket;
    }
    prevTime = t;
  }
  return ticks;
}
